// Altas de obra (obra nueva o duplicada). Una obra nueva vive solo en el navegador hasta
// que se guarda: hasta entonces lleva un codObra temporal. Al guardarla, el servidor le da
// su codObra definitivo y hay que reapuntar todo lo que iba por el temporal.
//
// Este módulo es la parte determinista de ese flujo (qué se envía y cómo se reapunta),
// separada de obras.jsx para poder probarla.

// Clave de un cambio pendiente (draft) en el estado de la ficha.
export const editKey = (codObra, field) => `${codObra}:${field}`;

// Los codObra temporales son negativos: SQL Server reparte identidades positivas, así que
// un temporal nunca puede coincidir con el de una obra real ni taparla al refrescar.
export const isTempObraId = (codObra) => Number(codObra) < 0;

export function nextTempObraId(obras = []) {
    return Math.min(0, ...obras.map((obra) => Number(obra?.codObra) || 0)) - 1;
}

// Cambios pendientes que entran en un guardado:
// - "batch": todos (botón "Guardar cambios" del dashboard).
// - "alta": TODOS los de esa obra. Una obra nueva se crea entera; si solo se enviara el
//   campo desde el que se pulsó guardar, la obra se daría de alta a medias y el resto de
//   cambios se quedarían apuntando a un codObra temporal que ya no existe.
// - "geography": solo país y ciudad de esa obra (se guardan juntos).
// En todos los casos se descartan los cambios de obras que ya no están en la cartera
// (alta descartada, obra borrada): no se pueden guardar, así que tampoco se anuncian
// después como guardados.
export function draftsToPersist(draftEdits = {}, changeRequest = {}, obras = []) {
    const ids = new Set(obras.map((obra) => obra.codObra));
    return Object.values(draftEdits).filter((draft) => {
        if (!ids.has(draft.codObra)) return false;
        if (changeRequest.kind === "batch") return true;
        if (draft.codObra !== changeRequest.codObra) return false;
        return changeRequest.kind === "alta" || ["pais", "ciudad"].includes(draft.field);
    });
}

// Pares [codObra temporal, obra guardada] -> mapa temporal -> definitivo (solo los que
// cambian de id: una edición normal devuelve el mismo codObra).
export function savedIdMap(saved = []) {
    return new Map(saved
        .filter(([tempId, obra]) => obra && obra.codObra !== tempId)
        .map(([tempId, obra]) => [tempId, obra.codObra]));
}

// Reapunta los cambios pendientes al codObra definitivo (la clave lo lleva dentro).
export function remapDrafts(draftEdits = {}, idMap = new Map()) {
    if (!idMap.size) return draftEdits;
    return Object.fromEntries(Object.entries(draftEdits).map(([key, draft]) => {
        if (!idMap.has(draft.codObra)) return [key, draft];
        const codObra = idMap.get(draft.codObra);
        const nextKey = editKey(codObra, draft.field);
        return [nextKey, { ...draft, key: nextKey, codObra }];
    }));
}

// Reapunta las entradas de historial locales (el alta se registra antes de conocer el id).
export function remapAuditEntries(entries = [], idMap = new Map()) {
    if (!idMap.size) return entries;
    return entries.map((entry) => (idMap.has(entry.codObra) ? { ...entry, codObra: idMap.get(entry.codObra) } : entry));
}

// Reapunta un conjunto de ids (selección de filas). Devuelve el mismo si no cambia nada.
export function remapIdSet(ids = new Set(), idMap = new Map()) {
    if (!idMap.size || ![...ids].some((id) => idMap.has(id))) return ids;
    return new Set([...ids].map((id) => (idMap.has(id) ? idMap.get(id) : id)));
}

// Claves de los cambios ya guardados, con el codObra definitivo. Tras un alta, las claves
// del estado se han reapuntado al id real, asi que borrarlas por la clave antigua no quita
// nada y los cambios recien guardados se quedan pendientes para siempre.
export function savedDraftKeys(drafts = [], idMap = new Map()) {
    return new Set(drafts.map((draft) => editKey(
        idMap.has(draft.codObra) ? idMap.get(draft.codObra) : draft.codObra,
        draft.field,
    )));
}

// Cambios pendientes cuya obra ya no existe: hay que retirarlos del estado.
export function pruneOrphanDrafts(draftEdits = {}, obras = []) {
    const ids = new Set(obras.map((obra) => obra.codObra));
    const keep = Object.entries(draftEdits).filter(([, draft]) => ids.has(draft.codObra));
    return keep.length === Object.keys(draftEdits).length ? draftEdits : Object.fromEntries(keep);
}
